import React, {useState} from "react";
import useEditableTableTextCell from "../../hooks/useEditableTableTextCell";
import useEditableTableDateCell from "../../hooks/useEditableTableDateCell";
import useEditableTableCostCell from "../../hooks/useEditableTableCostCell";
import { Button, Checkbox, Control, Input } from "@allxsmith/bestax-bulma";
import ContactSearcher from "../Utils/ContactSearcher";
import {getPartCustomerCost} from "../../utils";
import TrackingOptionsPicker from "../Settings/TrackingCategory/TrackingOptionsPicker";
import TaxTypePicker from "../Settings/NominalCodes/TaxTypePicker";



// Inbound carrier inferred from the Maxus consignment-number shape (mirrors Part.save() on the server):
// 10-digit -> DPD, starts "L" -> DX, starts "9" -> pallet network, else OTHER.
const inboundCarrier = p => {
    const t = (p.inbound_tracking_number || "").trim().toUpperCase();
    const d = t.replace(/ /g, "");
    if (!t) return "";
    if (t[0] === "9") return "PALLET";
    if (t[0] === "L") return "DX";
    if (/^[0-9]{10}$/.test(d)) return "DPD";
    return p.inbound_carrier || "OTHER";
};

export default function PartRow({part, onPartChange, onPartDelete, index, appDefaults, partOrder, disabled, trackingOptions, trackingOptionObjects}){
    const [editable, setEditable] = useState(true);
    const [searchData, setSearchData] = useState([]);

    const onChange = e => {
        let newPart = {...part, [e.target.name]: e.target.value};
        if(e.target.name === "cost"){
            newPart.customer_cost = getPartCustomerCost(newPart, partOrder.customer, appDefaults)
        }
        onPartChange(newPart, index);
    }

    const onTrackingOptionChange = e => {
        const toUpdate = {
            target: {
                name: e.target.name,
                value: trackingOptionObjects[e.target.value]
            }
        };
        onChange(toUpdate);
    }

    const onCheck = e => {
        let newPart = {...part, [e.target.name]: !part[e.target.name]}
        onPartChange(newPart, index);
    }

    const onSupplierSelect = e => {
        let newPart = {...part, supplier: e[0]}
        onPartChange(newPart, index)
    }

    return (
      <tr>
        {useEditableTableTextCell({
          propName: "name",
          value: part.name,
          onChange: onChange,
          editable,
          setEditable,
          disabled: part.approved || disabled,
          inlineEdit: false
        })}
        <td>
          <ContactSearcher
            type={"SP"}
            searchData={searchData}
            setSearchData={setSearchData}
            selected={part.supplier}
            disabled={!editable || part.approved || disabled}
            name={"supplier"}
            onSelect={onSupplierSelect}
          />
        </td>
        <td>
            <TrackingOptionsPicker
                options={trackingOptions}
                value={part.supplier_tracking_option}
                name={"supplier_tracking_option"}
                disabled={part.approved}
                editable={true}
                inlineEdit={false}
                blank={true}
                onChange={onTrackingOptionChange}
                className={" "}
            />
        </td>
        <td>
            <TrackingOptionsPicker
                options={trackingOptions}
                value={part.customer_tracking_option}
                name={"customer_tracking_option"}
                disabled={false}
                editable={true}
                inlineEdit={false}
                blank={true}
                onChange={onTrackingOptionChange}
                className={" "}
            />
        </td>
        {useEditableTableDateCell({
          propName: "date_ordered",
          value: part.date_ordered,
          onChange: onChange,
          editable,
          setEditable,
          disabled: part.approved || disabled,
          inlineEdit: false,
          monthOnly: false
        })}
        {useEditableTableTextCell({
          propName: "part_number",
          value: part.part_number,
          onChange: onChange,
          editable,
          setEditable,
          disabled: part.approved || disabled,
          inlineEdit: false
        })}
        {useEditableTableTextCell({
          propName: "part_invoice_number",
          value: part.part_invoice_number,
          onChange: onChange,
          editable,
          setEditable,
          disabled: part.approved || disabled,
          inlineEdit: false
        })}
        <td>
          <Control>
            <Input type="text" name="inbound_tracking_number" value={part.inbound_tracking_number || ""}
                   onChange={onChange} disabled={part.approved || disabled} />
          </Control>
        </td>
        <td>{inboundCarrier(part) ? <span className="tag is-info is-light">{inboundCarrier(part)}</span> : null}</td>
        <td>
          <Checkbox name={"arrived"} checked={part.arrived} disabled={part.approved || disabled} onClick={onCheck} />
        </td>
        {useEditableTableCostCell({
          propName: "cost",
          value: part.cost,
          onChange: onChange,
          editable,
          setEditable,
          disabled: part.approved || disabled,
          inlineEdit: false
        })}
        <td>
            <Control>
              <Input
                type="number"
                name="customer_cost"
                onChange={onChange}
                value={(Math.round(part.customer_cost * 100 +Number.EPSILON) / 100)}
                checked={part.customer_cost}
                disabled={!part.cost_override || part.approved || disabled}
              />
            </Control>
          </td>
        <td>
          <TaxTypePicker
            name={"cost_vat"}
            onChange={onChange}
            value={part.cost_vat}
            disabled={part.approved || disabled}
          />
        </td>
        {useEditableTableTextCell({
          propName: "comments",
          value: part.comments,
          onChange: onChange,
          editable,
          setEditable,
          disabled: part.approved || disabled,
          inlineEdit: false
        })}
        <td>
          <Checkbox
            name={"cost_override"}
            checked={part.cost_override}
            disabled={part.approved || disabled}
            onClick={onCheck}
          />
        </td>
        <td>
          <Checkbox
            name={"is_postage"}
            checked={part.is_postage}
            disabled={part.approved || disabled}
            onClick={onCheck}
          />
        </td>
        <td>
          <Checkbox
            name={"from_stock"}
            checked={part.from_stock}
            disabled={part.approved || disabled}
            onClick={onCheck}
          />
        </td>
        {useEditableTableDateCell({
          propName: "backorder_expected_date",
          value: part.backorder_expected_date,
          onChange: onChange,
          editable,
          setEditable,
          disabled: part.approved || disabled,
          inlineEdit: false
        })}
        <td>
          <Checkbox
            name={"to_be_ordered"}
            checked={part.to_be_ordered}
            onClick={onCheck}
            disabled={part.approved || disabled}
          />
        </td>
        <td>
          <Checkbox
            name={"non_returnable"}
            checked={part.non_returnable}
            onClick={onCheck}
            disabled={part.approved || disabled}
          />
        </td>
        <td>
            <Button
                color={"danger"}
                onClick={() => onPartDelete(part, index)}
                disabled={part.approved || disabled}
            >
                X
            </Button>
        </td>
      </tr>
    );




}